import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminAuth";
import { EtapaIcon } from "@/components/rastreo/EtapaIcon";
import { formatFechaHora } from "@/lib/format";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/common/ErrorState";

export default function Dashboard() {
  usePageTitle("Dashboard");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => adminApi.get("/admin/dashboard").then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl px-8 py-8" data-testid="dashboard-skeleton">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="mt-2 h-4 w-64" />
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-[var(--tc-radius)]" />
          ))}
        </div>
        <Skeleton className="mt-8 h-3 w-40" />
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-[var(--tc-radius)]" />
          ))}
        </div>
        <Skeleton className="mt-8 h-3 w-40" />
        <Skeleton className="mt-3 h-52 rounded-[var(--tc-radius)]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-6xl px-8 py-8">
        <ErrorState testId="dashboard-error" mensaje="No se pudo cargar el dashboard." onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-8 py-8">
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Dashboard</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">Resumen operativo de todas las unidades.</p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-4">
          <p className="text-xs font-medium text-[var(--tc-text-secondary)]">Unidades activas</p>
          <p data-testid={ADMIN.dashboardTotalActive} className="mt-1 text-2xl font-semibold text-[var(--tc-accent)]">
            {data.total_activas}
          </p>
        </div>
        <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-4">
          <p className="text-xs font-medium text-[var(--tc-text-secondary)]">Entregadas</p>
          <p data-testid={ADMIN.dashboardTotalDelivered} className="mt-1 text-2xl font-semibold text-[var(--tc-state-done)]">
            {data.total_entregadas}
          </p>
        </div>
        <div className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-4">
          <p className="text-xs font-medium text-[var(--tc-text-secondary)]">Total unidades</p>
          <p className="mt-1 text-2xl font-semibold text-[var(--tc-text)]">{data.total_unidades}</p>
        </div>
      </div>

      <h2 className="mt-8 text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
        Unidades por etapa
      </h2>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {data.conteos_por_etapa.map((c) => (
          <div
            key={c.etapa.id}
            data-testid={ADMIN.dashboardStageCard}
            className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-3.5"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
              <EtapaIcon nombre={c.etapa.icono} size={16} />
            </span>
            <p className="mt-2 text-lg font-semibold text-[var(--tc-text)]">{c.total}</p>
            <p className="truncate text-xs text-[var(--tc-text-secondary)]">{c.etapa.nombre}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-8 text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
        Últimas actualizadas
      </h2>
      <div className="mt-3 divide-y divide-[var(--tc-border)] rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {data.ultimas_actualizadas.map((u) => (
          <Link
            key={u.id}
            to={`/admin/unidades/${u.id}`}
            data-testid={ADMIN.dashboardRecentRow}
            className="flex items-center justify-between gap-4 px-4 py-3 transition-colors duration-150 hover:bg-[var(--tc-surface)]"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-[var(--tc-text)]">
                {u.marca} {u.modelo} <span className="font-mono text-xs tabular-nums text-[var(--tc-text-secondary)]">···{u.vin_last6}</span>
              </p>
              <p className="mt-0.5 text-xs tabular-nums text-[var(--tc-text-secondary)]">
                {u.cliente?.nombre} · {u.etapa_actual?.nombre} · {formatFechaHora(u.actualizado_en)}
              </p>
            </div>
            <ChevronRight size={16} className="shrink-0 text-[var(--tc-state-pending)]" />
          </Link>
        ))}
      </div>
    </div>
  );
}
